import React from 'react';

import { useIntl } from 'ha/i18n';

import { TypeFilter } from 'ha/components/Filters/TypeFilter';
import { GenderFilter } from 'ha/components/Filters/GenderFilter';
import { RegistrationFilter } from 'ha/components/Filters/RegistrationFilter';
import { FilterAccordion } from 'ha/components/Filters/FilterAccordion';
import { SearchAreaFilter } from 'ha/components/Filters/SearchAreaFilter';

import { SearchFiltersContext, EmptyState } from '../../SearchFiltersContext';

import { PriceFilter } from './PriceFilter';
import { FacilitiesFilter } from './FacilitiesFilter';
import { CountryFilter } from './CountryFilter';
import { MaximumStayFilter } from './MaximumStayFilter';
import { FreePlacesFilter } from './FreePlacesFilter';

const SearchFilters: React.FC = () => {
  const { T } = useIntl();

  const {
    localState,
    onChangeRooms,
    onChangeTypes,
    onChangePrice,
    onChangeGender,
    onChangeFacilities,
    onChangeCountry,
    onChangeFreePlaces,
    onChangeMaximumStay,
    onChangeSearchAreaFilter,
    onChangeRegistration,
    isFilterEmpty,
  } = React.useContext(SearchFiltersContext);
  const handleCountryClear = React.useCallback(() => {
    onChangeCountry(EmptyState.country);
  }, [onChangeCountry]);

  const handlePropertyTypeClear = React.useCallback(() => {
    onChangeTypes(EmptyState.types);
  }, [onChangeTypes]);

  const handleFacilitiesClear = React.useCallback(() => {
    onChangeFacilities(EmptyState.facilities);
  }, [onChangeFacilities]);

  const handleSuitableForClear = React.useCallback(() => {
    onChangeGender(EmptyState.gender);
  }, [onChangeGender]);

  const handlePriceClear = React.useCallback(() => {
    onChangePrice(EmptyState.price);
  }, [onChangePrice]);

  const handleFreePlacesClear = React.useCallback(() => {
    onChangeFreePlaces(EmptyState.freePlaces);
  }, [onChangeFreePlaces]);

  const handleMaximumStayClear = React.useCallback(() => {
    onChangeMaximumStay(EmptyState.maximumStay);
  }, [onChangeMaximumStay]);

  const handleRegistrationClear = React.useCallback(() => {
    onChangeRegistration(EmptyState.registration);
  }, [onChangeRegistration]);

  return (
    <React.Fragment>
      <FilterAccordion
        label={T('Country')}
        defaultExpanded
        dataTestLocator="Country Filter Accordion"
        onClear={isFilterEmpty('country') ? undefined : handleCountryClear}
        trackText="Search filter accordion clicked"
        topBorder
      >
        <CountryFilter value={localState.country} onChange={onChangeCountry} />
        <SearchAreaFilter
          value={localState.searchArea}
          onChange={onChangeSearchAreaFilter}
          location="filter"
        />
      </FilterAccordion>
      <FilterAccordion
        label={T('Price')}
        defaultExpanded
        dataTestLocator="Price"
        onClear={isFilterEmpty('price') ? undefined : handlePriceClear}
        trackText="Search filter accordion clicked"
        topBorder
      >
        <PriceFilter value={localState.price} onChange={onChangePrice} />
      </FilterAccordion>

      <FilterAccordion
        label={T('ukr_response.search.maximum_stay')}
        defaultExpanded
        dataTestLocator="MaximumStay"
        onClear={
          isFilterEmpty('maximumStay') ? undefined : handleMaximumStayClear
        }
        trackText="Search filter accordion clicked"
        topBorder
      >
        <MaximumStayFilter
          value={localState.maximumStay}
          onChange={onChangeMaximumStay}
        />
      </FilterAccordion>

      <FilterAccordion
        label={T('label.number_people')}
        defaultExpanded
        dataTestLocator="FreePlaces"
        onClear={
          isFilterEmpty('freePlaces') ? undefined : handleFreePlacesClear
        }
        trackText="Search filter accordion clicked"
        topBorder
      >
        <FreePlacesFilter
          value={localState.freePlaces}
          onChange={onChangeFreePlaces}
        />
      </FilterAccordion>

      <FilterAccordion
        label={T('Property type')}
        defaultExpanded
        dataTestLocator="Property Type"
        onClear={isFilterEmpty('types') ? undefined : handlePropertyTypeClear}
        trackText="Search filter accordion clicked"
        topBorder
      >
        <TypeFilter
          value={localState.types}
          onChange={onChangeTypes}
          roomValue={localState.rooms}
          onChangeRooms={onChangeRooms}
        />
      </FilterAccordion>

      <FilterAccordion
        trackText="Search filter accordion clicked"
        topBorder
        label={T('Facilities')}
        defaultExpanded={!isFilterEmpty('facilities')}
        dataTestLocator="Facilities Filter"
        onClear={
          isFilterEmpty('facilities') ? undefined : handleFacilitiesClear
        }
      >
        <FacilitiesFilter
          value={localState.facilities}
          onChange={onChangeFacilities}
        />
      </FilterAccordion>

      <FilterAccordion
        trackText="Search filter accordion clicked"
        topBorder
        label={T('Suitable for')}
        defaultExpanded={!isFilterEmpty('gender')}
        dataTestLocator="Suitable For"
        onClear={isFilterEmpty('gender') ? undefined : handleSuitableForClear}
      >
        <GenderFilter value={localState.gender} onChange={onChangeGender} />
      </FilterAccordion>
      <FilterAccordion
        trackText="Search filter accordion clicked"
        topBorder
        label={T('Registration')}
        defaultExpanded={!isFilterEmpty('registration')}
        dataTestLocator="Registration"
        onClear={
          isFilterEmpty('registration') ? undefined : handleRegistrationClear
        }
      >
        <RegistrationFilter
          value={localState.registration}
          onChange={onChangeRegistration}
        />
      </FilterAccordion>
    </React.Fragment>
  );
};

export { SearchFilters };
